import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { message } from "antd";
import moment from "moment";

export const exportToExcel = ({ data, employeeStatus }) => {
  if (!data || data.length === 0) {
    message.warning("No data to export");
    return;
  }

  const exportData = data.map((item, index) => {
    const joinDate = moment(item.join_date).startOf("day");
    const today = moment().startOf("day");

    let workDuration = "-";

    if (joinDate.isValid()) {
      let workEndDate;

      // Kalau sudah resign, hitung sampai tanggal resign
      if (item.resign_date && moment(item.resign_date).isValid()) {
        workEndDate = moment(item.resign_date).startOf("day");
      } else {
        // Kalau masih aktif, hitung sampai hari ini
        workEndDate = today.clone();
      }

      // Join date belum terjadi
      if (joinDate.isAfter(workEndDate)) {
        workDuration = "0 Years 0 Months 0 Days";
      } else {
        const tempDate = joinDate.clone();

        const years = workEndDate.diff(tempDate, "years");
        tempDate.add(years, "years");

        const months = workEndDate.diff(tempDate, "months");
        tempDate.add(months, "months");

        const days = workEndDate.diff(tempDate, "days");

        workDuration = `${years} Years ${months} Months ${days} Days`;
      }
    }
    return {
      "User Code": item.users_code,
      "Name": item.name,
      "Barcode": "",
      "Organization": item.division?.name || "-",
      "Position": item.position?.name || "-",
      "Job Level": item.level_detail?.name || "-",
      "Join Date": item.join_date ? moment(item.join_date).format('YYYY-MM-DD') : "-",
      "Resign Date": item.resign_date ? moment(item.resign_date).format('YYYY-MM-DD') : "-",

      "Employee Status": !isNaN(item.employee_status)
        ? employeeStatus.find(
          status => String(status.value) === String(item.employee_status)
        )?.label || item.employee_status
        : item.employee_status,

      "End Date": item.end_date ? moment(item.end_date).format('YYYY-MM-DD') : "-",
      "Sign Date": "",
      "Email": item.email,
      "Birthdate": moment(item.birthdate).format('YYYY-MM-DD'),
      "Age": "",
      "Birth Place": "",
      "Current Address": item.address_now,
      "Address": item.address,
      "NPWP": item.npwp,
      "PTKP Status": item.ptkp_status,
      "Employee Tax Status": "",
      "Tax Config": "Netto",
      "Bank Name": item.bank_name,
      "Bank Account": item.bank_account,
      "Account Name": item.bank_account_name,
      "BPJS Ketenagakerjaan": "",
      "BPJS Kesehatan": "",
      "NIK": item.nik,
      "Mobile Phone": item.phone,
      "Phone": item.phone,
      "Branch Name": item.branch_name,
      "Parent Branch Name": "",
      "Religion": item.religion,
      "Gender": item.gender,
      "Marital Status": item.marital_status,
      "Blood Type": item.bloody_type,
      "Nationality Code": "",
      "Currency": "Rp",
      "Work Duration": workDuration,
      "Payment Schedule": "",
      "Approval Line": item.approval_name,
      "Manager": "",
      "Grade": item.grade,
      "Class": item.class,
      "Picture": item.image !== null ? "true" : "false",
      "Cost Center": "",
      "Cost Center Category": "",
      "SBU": "",
      "NPWP Old": "",
      "Passport": "",
      "Passport Expiration Date": "",
      "Jenis Dok. Referensi Bukti Potong": "",
      "Nomor Dok. Referensi Bukti Potong": "",
      "Tanggal Dok. Referensi Bukti Potong": "",
      "Nama PT": "",
      "User Role": item.userrole?.name,
      "Education Level": (item.education_history || []).map(education => education.education_level || '').filter(Boolean).join('; '),
      "Education Institution": (item.education_history || []).map(education => education.institution_name || '').filter(Boolean).join('; '),
      "Education Major": (item.education_history || []).map(education => education.major || '').filter(Boolean).join('; '),
      "Education Start Year": (item.education_history || []).map(education => education.start_year || '').filter(Boolean).join('; '),
      "Education End Year": (item.education_history || []).map(education => education.end_year || '').filter(Boolean).join('; '),
      "Education GPA": (item.education_history || []).map(education => education.gpa || '').filter(Boolean).join('; '),
      "Education Notes": (item.education_history || []).map(education => education.description || '').filter(Boolean).join('; ')
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(exportData, { origin: 0 });

  const headerKeys = Object.keys(exportData[0]);

  // Header styling minimal (tanpa warna biru)
  headerKeys.forEach((key, idx) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx }); // row 0 sekarang
    if (!worksheet[cellRef]) return;

    worksheet[cellRef].s = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
      fill: { fgColor: { rgb: "1F4E78" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };
  });

  // Body styling tetap
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  for (let R = 1; R <= range.e.r; ++R) {
    for (let C = 0; C <= range.e.c; ++C) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
      if (!worksheet[cellRef]) continue;

      worksheet[cellRef].s = {
        border: {
          top: { style: "thin", color: { rgb: "CCCCCC" } },
          bottom: { style: "thin", color: { rgb: "CCCCCC" } },
          left: { style: "thin", color: { rgb: "CCCCCC" } },
          right: { style: "thin", color: { rgb: "CCCCCC" } },
        },
        alignment: { vertical: "center", horizontal: C === 0 ? "center" : "left" },
      };
    }
  }

  // Column width auto
  const colWidths = headerKeys.map((header) => {
    const maxHeaderLength = header.length;
    const maxDataLength = exportData.reduce((max, row) => {
      const cellValue = row[header] ? String(row[header]) : "";
      return cellValue.length > max ? cellValue.length : max;
    }, 0);
    return { wch: Math.max(maxHeaderLength, maxDataLength) + 2 };
  });
  worksheet["!cols"] = colWidths;

  // Export
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Employee");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(dataBlob, "employee_data.xlsx");
};
